<template>
  <b-card
    title="Финализация дистанции"
    class="mt-3 lifecycle-panel"
  >
    <p class="text-muted mb-2">
      Финализация фиксирует официальные результаты дистанции и блокирует обычные правки
      (анализ, ручные времена, виртуальные считывания, массовые НС/НФ, изменение конфигурации).
      Снятие финализации требует обязательной причины и записывается в журнал аудита.
    </p>

    <div class="d-flex align-items-center flex-wrap mb-3">
      <span class="mr-2 font-weight-bold">Текущий статус:</span>
      <b-badge
        :variant="statusBadgeVariant"
        class="lifecycle-status-badge"
      >
        {{ statusLabel }}
      </b-badge>
      <span
        v-if="finalizedAtFormatted"
        class="ml-3 small text-muted"
      >
        Финализировано: {{ finalizedAtFormatted }}
        <template v-if="state && state.finalizedByUserId != null">
          (пользователь #{{ state.finalizedByUserId }})
        </template>
      </span>
    </div>

    <div class="d-flex flex-wrap mb-3">
      <b-button
        v-if="!isFinalized"
        variant="warning"
        size="sm"
        :disabled="!canFinalize"
        class="mr-2"
        @click="openFinalizeModal"
      >
        Финализировать
      </b-button>
      <b-button
        v-else
        variant="outline-danger"
        size="sm"
        :disabled="!canUnfinalize"
        class="mr-2"
        @click="openUnfinalizeModal"
      >
        Снять финализацию
      </b-button>
      <b-button
        variant="outline-secondary"
        size="sm"
        :disabled="isLoading"
        @click="loadState"
      >
        Обновить
      </b-button>
    </div>

    <h6 class="mb-2">
      История ({{ recentEvents.length }})
    </h6>
    <p
      v-if="!recentEvents.length"
      class="small text-muted mb-0"
    >
      Событий журнала пока нет.
    </p>
    <ul
      v-else
      class="lifecycle-history mb-0 pl-3"
    >
      <li
        v-for="event in recentEvents"
        :key="event.id"
        class="small"
      >
        <strong>{{ formatEventType(event.eventType) }}</strong>
        <span class="text-muted ml-1">{{ formatEventDate(event.createdAt) }}</span>
        <span
          v-if="event.actorUserId != null"
          class="text-muted ml-1"
        >
          · пользователь #{{ event.actorUserId }}
        </span>
        <div
          v-if="event.reason"
          class="text-muted"
        >
          Причина: {{ event.reason }}
        </div>
      </li>
    </ul>

    <b-modal
      v-model="finalizeModal.visible"
      title="Финализировать дистанцию?"
      ok-title="Финализировать"
      cancel-title="Отмена"
      ok-variant="warning"
      :ok-disabled="isSaving"
      @ok="confirmFinalize"
    >
      <p>
        После финализации обычные правки этой дистанции будут отклонены сервером (HTTP 409).
        Чтобы вернуться к редактированию, потребуется снять финализацию с указанием причины.
      </p>
      <b-form-group label="Причина (опционально)">
        <b-form-textarea
          v-model="finalizeModal.reason"
          rows="2"
          maxlength="2000"
          placeholder="Например: подтверждены официальные результаты"
        />
      </b-form-group>
    </b-modal>

    <b-modal
      v-model="unfinalizeModal.visible"
      title="Снять финализацию дистанции?"
      ok-title="Снять финализацию"
      cancel-title="Отмена"
      ok-variant="danger"
      :ok-disabled="isSaving || !unfinalizeReasonValid"
      @ok="confirmUnfinalize"
    >
      <p>
        Снятие финализации временно открывает дистанцию для обычных правок.
        Причина обязательна и записывается в журнал аудита.
      </p>
      <b-form-group label="Причина">
        <b-form-textarea
          v-model="unfinalizeModal.reason"
          rows="2"
          maxlength="2000"
          placeholder="Например: исправление протокола после жалобы участника #34"
          :state="unfinalizeReasonValid ? null : false"
        />
      </b-form-group>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BBadge, BButton, BModal, BFormGroup, BFormTextarea,
} from 'bootstrap-vue'
import RaceAnalysisApiService from '@/services/race-analysis-api.service'

const STATUS_LABELS = {
  draft: 'Черновик',
  live: 'Идёт гонка',
  finalized: 'Финализировано',
}

const STATUS_VARIANTS = {
  draft: 'secondary',
  live: 'info',
  finalized: 'success',
}

const EVENT_LABELS = {
  finalize: 'Финализация',
  unfinalize: 'Снятие финализации',
  emergency_correction: 'Экстренная правка',
}

export default {
  name: 'LifecyclePanel',
  components: {
    BCard,
    BBadge,
    BButton,
    BModal,
    BFormGroup,
    BFormTextarea,
  },
  props: {
    startId: { type: [String, Number], default: null },
    distanceId: { type: [String, Number], default: null },
  },
  data() {
    return {
      state: null,
      recentEvents: [],
      isLoading: false,
      isSaving: false,
      finalizeModal: { visible: false, reason: '' },
      unfinalizeModal: { visible: false, reason: '' },
    }
  },
  computed: {
    statusLabel() {
      return STATUS_LABELS[this.state ? this.state.lifecycleStatus : 'draft'] || 'Неизвестно'
    },
    statusBadgeVariant() {
      return STATUS_VARIANTS[this.state ? this.state.lifecycleStatus : 'draft'] || 'secondary'
    },
    isFinalized() {
      return this.state && this.state.lifecycleStatus === 'finalized'
    },
    canFinalize() {
      return !!(this.startId && this.distanceId) && !this.isLoading && !this.isSaving
    },
    canUnfinalize() {
      return this.canFinalize && this.isFinalized
    },
    finalizedAtFormatted() {
      const raw = this.state && this.state.finalizedAt
      if (!raw) return ''
      try {
        return new Date(raw).toLocaleString()
      } catch (e) {
        return String(raw)
      }
    },
    unfinalizeReasonValid() {
      return !!(this.unfinalizeModal.reason && this.unfinalizeModal.reason.trim().length > 0)
    },
  },
  watch: {
    startId: {
      immediate: true,
      handler() { this.loadState() },
    },
    distanceId: {
      immediate: true,
      handler() { this.loadState() },
    },
  },
  methods: {
    async loadState() {
      if (!this.startId || !this.distanceId) {
        this.state = null
        this.recentEvents = []
        return
      }
      this.isLoading = true
      try {
        const data = await RaceAnalysisApiService.getLifecycleState(this.startId, this.distanceId)
        this.state = {
          startId: data.startId,
          distanceId: data.distanceId,
          lifecycleStatus: data.lifecycleStatus,
          finalizedAt: data.finalizedAt,
          finalizedByUserId: data.finalizedByUserId,
        }
        this.recentEvents = Array.isArray(data.recentEvents) ? data.recentEvents : []
        this.$emit('state-loaded', this.state)
      } catch (e) {
        if (e && e.response && e.response.status === 404) {
          this.state = null
          this.recentEvents = []
          this.$emit('state-loaded', null)
        } else {
          this.$emit('error', e)
        }
      } finally {
        this.isLoading = false
      }
    },

    openFinalizeModal() {
      this.finalizeModal.reason = ''
      this.finalizeModal.visible = true
    },

    openUnfinalizeModal() {
      this.unfinalizeModal.reason = ''
      this.unfinalizeModal.visible = true
    },

    async confirmFinalize(bvEvent) {
      if (this.isSaving) {
        bvEvent.preventDefault()
        return
      }
      this.isSaving = true
      try {
        const reason = (this.finalizeModal.reason || '').trim() || null
        await RaceAnalysisApiService.finalizeDistance(this.startId, this.distanceId, reason)
        this.finalizeModal.visible = false
        await this.loadState()
        this.$emit('changed', this.state)
      } catch (e) {
        bvEvent.preventDefault()
        this.$emit('error', e)
      } finally {
        this.isSaving = false
      }
    },

    async confirmUnfinalize(bvEvent) {
      if (this.isSaving || !this.unfinalizeReasonValid) {
        bvEvent.preventDefault()
        return
      }
      this.isSaving = true
      try {
        await RaceAnalysisApiService.unfinalizeDistance(
          this.startId,
          this.distanceId,
          this.unfinalizeModal.reason.trim(),
        )
        this.unfinalizeModal.visible = false
        await this.loadState()
        this.$emit('changed', this.state)
      } catch (e) {
        bvEvent.preventDefault()
        this.$emit('error', e)
      } finally {
        this.isSaving = false
      }
    },

    formatEventType(type) {
      return EVENT_LABELS[type] || type
    },

    formatEventDate(value) {
      if (!value) return ''
      try {
        return new Date(value).toLocaleString()
      } catch (e) {
        return String(value)
      }
    },
  },
}
</script>

<style scoped>
.lifecycle-history {
  list-style: disc;
}

.lifecycle-history li + li {
  margin-top: 0.25rem;
}

.lifecycle-status-badge {
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
